<template>
  <div v-if="selectedVorlage != null" class="alert alert-info">
    <font-awesome-icon icon="info-circle" /> <strong>Wichtig!</strong> Wenn Sie die Vorlage ändern, werden alle bisherigen Abschnitte und Eingaben gelöscht.
  </div>

  <div class="row mx-0 mb-3">
    <div class="col-4 offset-4">
      <div class="input-group input-group-sm my-auto">
        <input v-model.trim="searchString" type="search" class="form-control" id="searchInput" placeholder="Suche..." />
        <span class="input-group-text">
          <font-awesome-icon icon="magnifying-glass" class="p-1" />
        </span>
      </div>
    </div>
  </div>

  <div v-for="(value, key) in groupedVorlagen" :key="key" class="mb-4">
    <h5>{{ key }}</h5>

    <div class="row mx-0 mb-2">
      <div class="col-8 fw-bold my-auto">Titel</div>
      <div class="col-4 fw-bold my-auto">Abschnitte</div>
    </div>

    <div
      v-for="vorlage in value"
      :key="vorlage.id"
      class="row mx-0 mb-2 py-3 shadow-sm vorlageRow"
      :class="{ selectedVorlage: vorlage.id == selectedVorlage?.id }"
      @click="selectVorlage(vorlage.id)"
    >
      <div class="col-8">
        <strong>{{ vorlage.name }}</strong> ({{ vorlage.titel }})
      </div>

      <div class="col-4">{{ vorlage.vorlageTextbausteine.length }}</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  //#region Imports
  import { PropType, computed, onMounted, ref } from 'vue';
  import { useStore } from '@/store';

  import { Textbaustein, Vorlage, VorlageTextbaustein, GroupedVorlagen } from '@/models/VorlageModels';
  import { Input, TextAbschnitt, Vertrag } from '@/models/VertragModels';

  //#endregion

  const { editVertrag } = defineProps({
    editVertrag: {
      type: Object as PropType<Vertrag>,
      required: true,
    },
  });

  const emit = defineEmits(['vorlageSelected']);

  const store = useStore();

  //#region Vorlage
  const searchString = ref('');

  const vorlagen = computed(() => store.getters.vorlagen);

  const sortedVorlagen = computed(() => {
    return vorlagen.value
      .filter((v: Vorlage) => {
        return (
          v.name.trim().toLowerCase().includes(searchString.value.trim().toLowerCase()) ||
          v.titel.trim().toLowerCase().includes(searchString.value.trim().toLowerCase()) ||
          v.gruppe.trim().toLowerCase().includes(searchString.value.trim().toLowerCase())
        );
      })
      .sort((a: Vorlage, b: Vorlage) => a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase()));
  });

  const groupedVorlagen = computed<GroupedVorlagen>(() => {
    const noGroup: Vorlage[] = [];

    const data = sortedVorlagen.value.reduce((groups: GroupedVorlagen, vorlage: Vorlage) => {
      if (!vorlage.gruppe) {
        noGroup.push(vorlage);
      } else {
        const groupsList = vorlage.gruppe.split(';');

        for (let group of groupsList) {
          group = group.trim();

          if (group === '') {
            continue;
          }

          const existingGroup = groups[group];

          if (existingGroup) {
            existingGroup.push(vorlage);
          } else {
            groups[group] = [vorlage];
          }
        }
      }
      return groups;
    }, {});

    // Sort keys alphabetically
    const sortedData: GroupedVorlagen = {};
    Object.keys(data)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .forEach((key) => {
        sortedData[key] = data[key];
      });

    if (noGroup.length > 0) {
      sortedData['Keine Gruppe'] = noGroup;
    }

    return sortedData;
  });

  const selectedVorlage = ref<Vorlage>();

  function selectVorlage(id: number) {
    if (editVertrag.vorlageid != id) {
      selectedVorlage.value = JSON.parse(JSON.stringify(vorlagen.value.find((v: Vorlage) => v.id == id))) as Vorlage;

      editVertrag.titel = selectedVorlage.value.titel;
      editVertrag.titelEN = selectedVorlage.value.titelEN;
      editVertrag.vorlageid = selectedVorlage.value.id;
      editVertrag.textabschnitte = new Array<TextAbschnitt>();
      editVertrag.inputs = new Array<Input>();
      editVertrag.type = selectedVorlage.value.type;
      editVertrag.docId = selectedVorlage.value.docId;
      editVertrag.actionId = selectedVorlage.value.actionId;

      selectedVorlage.value?.vorlageTextbausteine.forEach((abschnitt: VorlageTextbaustein) => {
        const vertragSubAbschnitt = new Array<TextAbschnitt>();
        abschnitt.textbaustein.abschnitte.forEach((subabschnitt: Textbaustein) => {
          const subAbschnittRegeln = abschnitt.abschnittRegeln.find((ar) => ar.textbausteinId == subabschnitt.id);
          const subAbschnittSelected = subAbschnittRegeln != undefined && subAbschnittRegeln.aktiv && !subAbschnittRegeln.optional ? true : false;

          // Ebene 2
          const newSubabschnitt = new TextAbschnitt(
            0,
            editVertrag.id,
            subabschnitt.position,
            subAbschnittSelected,
            subabschnitt.id,
            null,
            new Array<TextAbschnitt>()
          );
          newSubabschnitt.textbaustein = subabschnitt;
          vertragSubAbschnitt.push(newSubabschnitt);
        });

        const abschnittRegeln = abschnitt.abschnittRegeln.find((ar) => ar.textbausteinId == abschnitt.textbausteinid);
        const abschnittSelected = abschnittRegeln != undefined && abschnittRegeln.aktiv && !abschnittRegeln.optional ? true : false;

        // Ebene 1
        const vertragAbschnitt = new TextAbschnitt(
          0,
          editVertrag.id,
          abschnitt.position,
          abschnittSelected,
          abschnitt.textbaustein.id,
          null,
          vertragSubAbschnitt
        );
        vertragAbschnitt.textbaustein = abschnitt.textbaustein;

        if (editVertrag.textabschnitte.findIndex((a: TextAbschnitt) => a.textbausteinid == abschnitt.textbaustein.id) == -1) {
          editVertrag.textabschnitte.push(vertragAbschnitt);
        }
      });
    }

    emit('vorlageSelected', selectedVorlage.value?.id);
  }

  function initData() {
    selectedVorlage.value = store.getters.vorlagen.find((v: Vorlage) => v.id == editVertrag.vorlageid);
  }

  onMounted(() => {
    initData();
  });
</script>

<style scoped>
  .vorlageRow {
    background: white;
    cursor: pointer;
  }

  .vorlageRow:hover {
    /* background: #ffd1df;
  color: #770025; */
    background: #fff4af;
  }

  .selectedVorlage {
    /* background: #ffd1df;
  color: #770025; */
    background-color: #fff4af;
  }
</style>
